/* eslint-disable @typescript-eslint/ban-types */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ProgressBar } from '../components/ProgressBar';
import { AbandonButton } from '../components/AbandonButton';
import { CommonStepButtons } from '../components/CommonStepButtons';
import { QuestionWrapper } from '../components/QuestionWrapper';
import { OuterScreenWrapper, ScreenWrapper } from '../components/ScreenWrapper';
import { IntakeFormProps } from '../models/intakeForm';
import { getQuestionDataFor } from '../selectors/intakeFormSelectors';
import { captureException } from 'src/shared/helpers/captureException';
import { BodyTextSmall, HeadingText30, RangePicker, pxToRem } from '@modernhealth/vital-components';
import styled from '@emotion/styled';

const Question = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    marginBottom: pxToRem(24),
});

export const IntakeNumericalResponseScreen: React.FC<IntakeFormProps> = ({
    currentScreen,
    responses,
    screenCount,
    onAbandonClick,
    skipQuestion,
    saveMultipleResponses,
    goBack,
}) => {
    const { questionText, questionIds, screenIndex } = getQuestionDataFor(currentScreen);
    const [responseObj, setResponseObj] = useState(responses || {});
    const { t } = useTranslation();

    const submitResponse = () => {
        saveMultipleResponses(responseObj as object);
    };

    const handleAbandonClick = () => {
        onAbandonClick(responseObj);
    };

    if (!questionIds) {
        captureException(
            new Error(
                'Received malformed or empty question data for multiple rating question screen',
            ),
            { currentScreen },
        );
        skipQuestion();
        return null;
    }

    return (
        <OuterScreenWrapper>
            <ScreenWrapper>
                <ProgressBar progress={screenIndex / screenCount} />
                <AbandonButton onClick={handleAbandonClick} />
                <QuestionWrapper title={t(questionText)} scrollContent>
                    {questionIds.map(({ subQuestionId, subQuestionSubtitle, subQuestionTitle }) => (
                        <Question key={subQuestionTitle}>
                            <HeadingText30 fontColor="gray85">{t(subQuestionTitle)}</HeadingText30>
                            <BodyTextSmall fontColor="gray65" marginBottom={20} marginTop={5}>
                                {t(subQuestionSubtitle)}
                            </BodyTextSmall>
                            <RangePicker
                                labelledBy={subQuestionTitle}
                                range={5}
                                minLabel={t('intakeForms:numerical_rating.least_important')}
                                maxLabel={t('intakeForms:numerical_rating.most_important')}
                                onValueChange={value =>
                                    setResponseObj({
                                        ...(responseObj as object),
                                        [subQuestionId]: value,
                                    })
                                }
                                value={responseObj[subQuestionId] ?? undefined}
                            />
                        </Question>
                    ))}
                    <CommonStepButtons onForwardClick={submitResponse} onBackClick={goBack} />
                </QuestionWrapper>
            </ScreenWrapper>
        </OuterScreenWrapper>
    );
};
